import { useQuery } from "@tanstack/react-query";
import { catalogApi } from "@/features/catalog/api";
import type { ProductQuery } from "@/features/catalog/types";

/**
 * Stable query-key factory. Grouping keys keeps invalidation scoped and safe.
 */
export const catalogKeys = {
    all: ["catalog"] as const,
    categories: () => [...catalogKeys.all, "categories"] as const,
    category: (slug: string) => [...catalogKeys.all, "category", slug] as const,
    products: (q: ProductQuery) => [...catalogKeys.all, "products", q] as const,
    product: (id: string) => [...catalogKeys.all, "product", id] as const,
};

export function useCategories() {
    return useQuery({
        queryKey: catalogKeys.categories(),
        queryFn: catalogApi.listCategories,
        staleTime: 5 * 60_000,
    });
}

export function useCategory(slug: string) {
    return useQuery({
        queryKey: catalogKeys.category(slug),
        queryFn: () => catalogApi.getCategory(slug),
        enabled: !!slug,
        staleTime: 5 * 60_000,
    });
}

export function useProducts(query: ProductQuery = {}) {
    return useQuery({
        queryKey: catalogKeys.products(query),
        queryFn: () => catalogApi.listProducts(query),
        staleTime: 60_000,
    });
}

export function useProduct(idOrSlug: string) {
    return useQuery({
        queryKey: catalogKeys.product(idOrSlug),
        queryFn: () => catalogApi.getProduct(idOrSlug),
        enabled: !!idOrSlug,
        staleTime: 2 * 60_000,
    });
}
